import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { Plus, X, Search, ExternalLink, Link2, RefreshCw } from "react-feather";
import {
  IAffiliateAdvertiser,
  IAdvertisersResponse,
  IAffiliateProduct,
  IProductsResponse,
  ICreateManualAdvertiserRequest,
  ICreateManualProductRequest,
  IProductUrlScrapeRequest,
  IProductUrlScrapeResult,
  IScrapedProductData,
  IFingerprintSearchResult,
} from "../../interfaces";
import {
  useGet,
  useGetLazy,
  usePost,
  useDelete,
  apiErrorMessage,
} from "../../api";

const PAGE_SIZE = 25;
const MANUAL_NETWORK = "Manual";

const trimmed = (value: string) => {
  const next = value.trim();
  return next.length > 0 ? next : undefined;
};

const formatPrice = (product: IAffiliateProduct) => {
  const amount = product.salePrice ?? product.price;
  if (amount == null) return "-";
  return `${product.currency ? `${product.currency} ` : ""}${amount.toFixed(2)}`;
};

/** Adapts a stored product into the fingerprint result shape the existing
 *  targeting handler consumes, so the target payload stays identical. */
const toFingerprintResult = (
  product: IAffiliateProduct,
  domain: string
): IFingerprintSearchResult => ({
  product: {
    id: product.id,
    name: product.name,
    domain: product.merchantDomain || domain,
    productUrl: product.productUrl || undefined,
    categories: product.categories || [],
    affiliateNetwork: product.affiliateNetwork,
    advertiserId: product.advertiserId,
    advertiserName: product.merchantName || undefined,
    manufacturer: product.manufacturer || undefined,
    sku: product.sku || undefined,
    ean: product.ean || undefined,
    mpn: product.mpn || undefined,
    canonicalKey: product.canonicalKey ?? null,
  },
  matchType: "manual",
  matchReason: "Selected from advertiser product list",
  matchedKey: product.id,
  matchConfidence: 1,
});

// ── Shared product form ─────────────────────────────────

interface ProductFormState {
  name: string;
  manufacturer: string;
  categories: string;
  price: string;
  currency: string;
  inStock: boolean;
  sku: string;
  ean: string;
  productUrl: string;
  imageUrl: string;
  description: string;
}

const emptyProductForm = (currency?: string | null): ProductFormState => ({
  name: "",
  manufacturer: "",
  categories: "",
  price: "",
  currency: currency || "",
  inStock: true,
  sku: "",
  ean: "",
  productUrl: "",
  imageUrl: "",
  description: "",
});

const scrapedToProductForm = (
  scraped: IScrapedProductData,
  fallbackCurrency?: string | null
): ProductFormState => ({
  name: scraped.name || "",
  manufacturer: scraped.manufacturer || "",
  categories: (scraped.categories || []).join(", "),
  price: scraped.price != null ? String(scraped.price) : "",
  currency: scraped.currency || fallbackCurrency || "",
  inStock: scraped.inStock !== false,
  sku: scraped.sku || "",
  ean: scraped.ean || "",
  productUrl: scraped.productUrl || "",
  imageUrl: scraped.imageUrl || "",
  description: scraped.description || "",
});

const toCreateProductRequest = (
  form: ProductFormState
): ICreateManualProductRequest => {
  const price = Number.parseFloat(form.price);
  const categories = form.categories
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);

  return {
    name: form.name.trim(),
    description: trimmed(form.description),
    categories: categories.length > 0 ? categories : undefined,
    price: Number.isFinite(price) ? price : undefined,
    currency: trimmed(form.currency),
    inStock: form.inStock,
    manufacturer: trimmed(form.manufacturer),
    sku: trimmed(form.sku),
    ean: trimmed(form.ean),
    productUrl: trimmed(form.productUrl),
    imageUrl: trimmed(form.imageUrl),
  };
};

const ProductFormFields: React.FC<{
  value: ProductFormState;
  onChange: (next: ProductFormState) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const set = <K extends keyof ProductFormState>(
    key: K,
    next: ProductFormState[K]
  ) => onChange({ ...value, [key]: next });

  return (
    <div className="row g-2">
      <div className="col-12">
        <Form.Label className="small mb-1">Name *</Form.Label>
        <Form.Control
          size="sm"
          value={value.name}
          disabled={disabled}
          onChange={(e) => set("name", e.target.value)}
        />
      </div>
      <div className="col-md-6">
        <Form.Label className="small mb-1">Manufacturer</Form.Label>
        <Form.Control
          size="sm"
          value={value.manufacturer}
          disabled={disabled}
          onChange={(e) => set("manufacturer", e.target.value)}
        />
      </div>
      <div className="col-md-6">
        <Form.Label className="small mb-1">
          Categories (comma separated)
        </Form.Label>
        <Form.Control
          size="sm"
          value={value.categories}
          disabled={disabled}
          onChange={(e) => set("categories", e.target.value)}
        />
      </div>
      <div className="col-md-4">
        <Form.Label className="small mb-1">Price</Form.Label>
        <Form.Control
          size="sm"
          type="number"
          step="0.01"
          value={value.price}
          disabled={disabled}
          onChange={(e) => set("price", e.target.value)}
        />
      </div>
      <div className="col-md-4">
        <Form.Label className="small mb-1">Currency</Form.Label>
        <Form.Control
          size="sm"
          value={value.currency}
          placeholder="GBP"
          disabled={disabled}
          onChange={(e) => set("currency", e.target.value)}
        />
      </div>
      <div className="col-md-4 d-flex align-items-end">
        <Form.Check
          type="checkbox"
          label="In stock"
          className="small mb-1"
          checked={value.inStock}
          disabled={disabled}
          onChange={(e) => set("inStock", e.target.checked)}
        />
      </div>
      <div className="col-md-6">
        <Form.Label className="small mb-1">SKU</Form.Label>
        <Form.Control
          size="sm"
          value={value.sku}
          disabled={disabled}
          onChange={(e) => set("sku", e.target.value)}
        />
      </div>
      <div className="col-md-6">
        <Form.Label className="small mb-1">EAN</Form.Label>
        <Form.Control
          size="sm"
          value={value.ean}
          disabled={disabled}
          onChange={(e) => set("ean", e.target.value)}
        />
      </div>
      <div className="col-12">
        <Form.Label className="small mb-1">Product URL</Form.Label>
        <Form.Control
          size="sm"
          value={value.productUrl}
          placeholder="https://..."
          disabled={disabled}
          onChange={(e) => set("productUrl", e.target.value)}
        />
      </div>
      <div className="col-12">
        <Form.Label className="small mb-1">Image URL</Form.Label>
        <Form.Control
          size="sm"
          value={value.imageUrl}
          placeholder="https://..."
          disabled={disabled}
          onChange={(e) => set("imageUrl", e.target.value)}
        />
      </div>
      <div className="col-12">
        <Form.Label className="small mb-1">Description</Form.Label>
        <Form.Control
          size="sm"
          as="textarea"
          rows={2}
          value={value.description}
          disabled={disabled}
          onChange={(e) => set("description", e.target.value)}
        />
      </div>
    </div>
  );
};

// ── Add manual advertiser ───────────────────────────────

const AddAdvertiserModal: React.FC<{
  show: boolean;
  domain: string;
  onHide: () => void;
  onCreated: (advertiser: IAffiliateAdvertiser) => void;
}> = ({ show, domain, onHide, onCreated }) => {
  const [form, setForm] = useState({
    advertiserId: "",
    name: "",
    domain,
    region: "",
    currency: "",
    websiteUrl: "",
    imageUrl: "",
    description: "",
  });
  const [error, setError] = useState<string | null>(null);
  const createAdvertiser = usePost<
    IAffiliateAdvertiser,
    ICreateManualAdvertiserRequest
  >();

  useEffect(() => {
    if (show) {
      setForm({
        advertiserId: "",
        name: "",
        domain,
        region: "",
        currency: "",
        websiteUrl: "",
        imageUrl: "",
        description: "",
      });
      setError(null);
    }
  }, [show, domain]);

  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    setError(null);
    try {
      const created = await createAdvertiser.execute(
        "affiliate-products/advertisers",
        {
          advertiserId: trimmed(form.advertiserId),
          name: form.name.trim(),
          region: trimmed(form.region),
          websiteUrl: trimmed(form.websiteUrl),
          domain: trimmed(form.domain),
          description: trimmed(form.description),
          imageUrl: trimmed(form.imageUrl),
          currency: trimmed(form.currency),
        }
      );
      if (created) onCreated(created);
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to create advertiser."));
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">Add Manual Advertiser</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="py-2 small">
            {error}
          </Alert>
        )}
        <div className="row g-2">
          <div className="col-md-6">
            <Form.Label className="small mb-1">
              Advertiser ID (optional)
            </Form.Label>
            <Form.Control
              size="sm"
              value={form.advertiserId}
              onChange={(e) => set("advertiserId", e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <Form.Label className="small mb-1">Name *</Form.Label>
            <Form.Control
              size="sm"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <Form.Label className="small mb-1">Domain</Form.Label>
            <Form.Control
              size="sm"
              value={form.domain}
              onChange={(e) => set("domain", e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <Form.Label className="small mb-1">Region</Form.Label>
            <Form.Control
              size="sm"
              placeholder="GB"
              value={form.region}
              onChange={(e) => set("region", e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <Form.Label className="small mb-1">Currency</Form.Label>
            <Form.Control
              size="sm"
              placeholder="GBP"
              value={form.currency}
              onChange={(e) => set("currency", e.target.value)}
            />
          </div>
          <div className="col-12">
            <Form.Label className="small mb-1">Website URL</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://..."
              value={form.websiteUrl}
              onChange={(e) => set("websiteUrl", e.target.value)}
            />
          </div>
          <div className="col-12">
            <Form.Label className="small mb-1">Image / Logo URL</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://..."
              value={form.imageUrl}
              onChange={(e) => set("imageUrl", e.target.value)}
            />
          </div>
          <div className="col-12">
            <Form.Label className="small mb-1">Description</Form.Label>
            <Form.Control
              size="sm"
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={handleSubmit}
          disabled={createAdvertiser.loading}
        >
          {createAdvertiser.loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <Plus size={12} className="me-1" />
              Create Advertiser
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Change advertiser ───────────────────────────────────

const ChangeAdvertiserModal: React.FC<{
  show: boolean;
  domain: string;
  currentId?: string;
  onHide: () => void;
  onSelect: (advertiser: IAffiliateAdvertiser) => void;
}> = ({ show, domain, currentId, onHide, onSelect }) => {
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const search = useGetLazy<IAdvertisersResponse>();

  useEffect(() => {
    if (!show) return;
    setQuery("");
    setError(null);
    search
      .execute(
        `affiliate-products/advertisers?domain=${encodeURIComponent(domain)}&limit=50`
      )
      .catch((err) =>
        setError(apiErrorMessage(err, "Failed to load advertisers."))
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, domain]);

  const handleSearch = async () => {
    setError(null);
    const q = query.trim();
    try {
      await search.execute(
        `affiliate-products/advertisers?limit=50${q ? `&q=${encodeURIComponent(q)}` : `&domain=${encodeURIComponent(domain)}`}`
      );
    } catch (err) {
      setError(apiErrorMessage(err, "Advertiser search failed."));
    }
  };

  const results = search.data?.advertisers || [];

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">Change Advertiser</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputGroup size="sm" className="mb-2">
          <Form.Control
            placeholder="Search advertisers by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <Button
            variant="outline-secondary"
            onClick={handleSearch}
            disabled={search.loading}
          >
            <Search size={12} />
          </Button>
        </InputGroup>

        {error && (
          <Alert variant="danger" className="py-2 small">
            {error}
          </Alert>
        )}

        {search.loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-muted small py-2">No advertisers found.</div>
        ) : (
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            <Table hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Network</th>
                  <th>Domain</th>
                  <th>Products</th>
                </tr>
              </thead>
              <tbody>
                {results.map((advertiser) => (
                  <tr
                    key={advertiser.id}
                    style={{ cursor: "pointer" }}
                    className={
                      advertiser.advertiserId === currentId
                        ? "table-active"
                        : ""
                    }
                    onClick={() => onSelect(advertiser)}
                  >
                    <td className="small fw-semibold">
                      {advertiser.name}
                      <small className="text-muted d-block">
                        ID: {advertiser.advertiserId}
                      </small>
                    </td>
                    <td className="small">{advertiser.affiliateNetwork}</td>
                    <td className="small">{advertiser.domain || "-"}</td>
                    <td className="small">{advertiser.productCount}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Add manual product ──────────────────────────────────

const AddProductModal: React.FC<{
  show: boolean;
  advertiser: IAffiliateAdvertiser;
  onHide: () => void;
  onCreated: () => void;
}> = ({ show, advertiser, onHide, onCreated }) => {
  const [form, setForm] = useState<ProductFormState>(
    emptyProductForm(advertiser.currency)
  );
  const [error, setError] = useState<string | null>(null);
  const createProduct = usePost<
    IAffiliateProduct,
    ICreateManualProductRequest
  >();

  useEffect(() => {
    if (show) {
      setForm(emptyProductForm(advertiser.currency));
      setError(null);
    }
  }, [show, advertiser.currency]);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    setError(null);
    try {
      await createProduct.execute(
        `affiliate-products/products/${encodeURIComponent(advertiser.advertiserId)}`,
        toCreateProductRequest(form)
      );
      onCreated();
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to create product."));
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">
          Add Product Manually · {advertiser.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="py-2 small">
            {error}
          </Alert>
        )}
        <ProductFormFields value={form} onChange={setForm} />
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={handleSubmit}
          disabled={createProduct.loading}
        >
          {createProduct.loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <Plus size={12} className="me-1" />
              Create Product
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Fetch & analyse from URL ────────────────────────────

const ScrapeUrlModal: React.FC<{
  show: boolean;
  advertiser: IAffiliateAdvertiser;
  onHide: () => void;
  onCreated: () => void;
}> = ({ show, advertiser, onHide, onCreated }) => {
  const [url, setUrl] = useState("");
  const [scraped, setScraped] = useState<IProductUrlScrapeResult | null>(null);
  const [form, setForm] = useState<ProductFormState>(
    emptyProductForm(advertiser.currency)
  );
  const [error, setError] = useState<string | null>(null);
  const scrape = usePost<IProductUrlScrapeResult, IProductUrlScrapeRequest>();
  const createProduct = usePost<
    IAffiliateProduct,
    ICreateManualProductRequest
  >();

  useEffect(() => {
    if (show) {
      setUrl("");
      setScraped(null);
      setForm(emptyProductForm(advertiser.currency));
      setError(null);
    }
  }, [show, advertiser.currency]);

  const handleScrape = async () => {
    if (!url.trim()) {
      setError("A product URL is required.");
      return;
    }
    setError(null);
    try {
      const result = await scrape.execute("affiliate-products/scrape-url", {
        url: url.trim(),
        advertiserId: advertiser.advertiserId,
      });
      if (result) {
        setScraped(result);
        setForm(scrapedToProductForm(result.product, advertiser.currency));
      }
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to analyse the URL."));
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    setError(null);
    try {
      await createProduct.execute(
        `affiliate-products/products/${encodeURIComponent(advertiser.advertiserId)}`,
        toCreateProductRequest(form)
      );
      onCreated();
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to save product."));
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">
          Fetch &amp; Analyse from URL
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputGroup size="sm" className="mb-2">
          <InputGroup.Text>
            <Link2 size={12} />
          </InputGroup.Text>
          <Form.Control
            placeholder="https://merchant.com/product/123"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleScrape();
            }}
          />
          <Button
            variant="outline-primary"
            onClick={handleScrape}
            disabled={scrape.loading}
          >
            {scrape.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <RefreshCw size={12} className="me-1" />
                Fetch &amp; Analyse
              </>
            )}
          </Button>
        </InputGroup>

        {error && (
          <Alert variant="danger" className="py-2 small">
            {error}
          </Alert>
        )}

        {scrape.loading && (
          <div className="text-muted small">
            Fetching and analysing the page — this can take a few seconds.
          </div>
        )}

        {scraped && (
          <>
            {scraped.advertiser && (
              <Alert variant="info" className="py-2 small">
                Detected store: <strong>{scraped.advertiser.name}</strong>
                {scraped.advertiser.domain
                  ? ` · ${scraped.advertiser.domain}`
                  : ""}
                {scraped.advertiser.currency
                  ? ` · ${scraped.advertiser.currency}`
                  : ""}
              </Alert>
            )}
            <div className="small text-muted mb-2">
              Review the extracted data before saving it to{" "}
              <strong>{advertiser.name}</strong>.
            </div>
            <ProductFormFields value={form} onChange={setForm} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={handleSave}
          disabled={!scraped || createProduct.loading}
        >
          {createProduct.loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <Plus size={12} className="me-1" />
              Save as Product
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Advertiser panel ────────────────────────────────────

export interface AdvertiserPanelProps {
  domain: string;
  /** Reuses the host panel's product-target handler so the payload is identical. */
  onAddProductTarget: (match: IFingerprintSearchResult) => Promise<void>;
  /** True when no coupon is selected to receive the target. */
  addTargetDisabled?: boolean;
  /** Keep advertiser data visible but prevent writes until the row is claimed. */
  readOnly?: boolean;
  targetedProductKeys?: Set<string>;
}

export const AdvertiserPanel: React.FC<AdvertiserPanelProps> = ({
  domain,
  onAddProductTarget,
  addTargetDisabled = false,
  readOnly = false,
  targetedProductKeys,
}) => {
  const [selectedAdvertiser, setSelectedAdvertiser] =
    useState<IAffiliateAdvertiser | null>(null);
  const [resolvedDomain, setResolvedDomain] = useState<string | null>(null);
  const [showAddAdvertiser, setShowAddAdvertiser] = useState(false);
  const [showChangeAdvertiser, setShowChangeAdvertiser] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showScrapeUrl, setShowScrapeUrl] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [productOffset, setProductOffset] = useState(0);
  const [addingTargetId, setAddingTargetId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const advertisersUrl = useMemo(
    () =>
      `affiliate-products/advertisers?domain=${encodeURIComponent(domain)}&limit=50`,
    [domain]
  );
  const advertisers = useGet<IAdvertisersResponse>(advertisersUrl);

  const productsUrl = useMemo(() => {
    if (!selectedAdvertiser) return null;
    const params = new URLSearchParams({
      advertiserId: selectedAdvertiser.advertiserId,
      network: selectedAdvertiser.affiliateNetwork,
      limit: String(PAGE_SIZE),
      offset: String(productOffset),
    });
    if (submittedQuery) params.set("q", submittedQuery);
    return `affiliate-products/products?${params.toString()}`;
  }, [selectedAdvertiser, productOffset, submittedQuery]);

  const products = useGet<IProductsResponse>(productsUrl, !!productsUrl);
  const deleteProduct = useDelete<void>();

  // Auto-select an advertiser once per domain, leaving manual picks intact.
  useEffect(() => {
    if (advertisers.loading) return;
    if (resolvedDomain === domain) return;
    setSelectedAdvertiser(advertisers.data?.advertisers?.[0] ?? null);
    setResolvedDomain(domain);
  }, [domain, resolvedDomain, advertisers.loading, advertisers.data]);

  useEffect(() => {
    setProductOffset(0);
    setProductQuery("");
    setSubmittedQuery("");
  }, [selectedAdvertiser?.id]);

  const handleSelectAdvertiser = (advertiser: IAffiliateAdvertiser) => {
    setSelectedAdvertiser(advertiser);
    setShowChangeAdvertiser(false);
    setActionError(null);
  };

  const handleAddTarget = async (product: IAffiliateProduct) => {
    setActionError(null);
    setAddingTargetId(product.id);
    try {
      await onAddProductTarget(toFingerprintResult(product, domain));
    } catch (err) {
      setActionError(apiErrorMessage(err, "Failed to add product target."));
    } finally {
      setAddingTargetId(null);
    }
  };

  const handleDeleteProduct = async (product: IAffiliateProduct) => {
    setActionError(null);
    try {
      await deleteProduct.execute(
        `affiliate-products/products/${encodeURIComponent(product.advertiserId)}/${encodeURIComponent(product.id)}`
      );
      products.refetch().catch(() => undefined);
    } catch (err) {
      setActionError(apiErrorMessage(err, "Failed to remove product."));
    }
  };

  const handleProductCreated = () => {
    setShowAddProduct(false);
    setShowScrapeUrl(false);
    setProductOffset(0);
    products.refetch().catch(() => undefined);
  };

  const productRows = products.data?.products || [];
  const productTotal = products.data?.totalCount ?? 0;
  const hasPrev = productOffset > 0;
  const hasNext = productOffset + PAGE_SIZE < productTotal;
  // Auto-selecting the first match is only safe when it's the only match.
  const domainAdvertiserCount = advertisers.data?.advertisers?.length ?? 0;

  return (
    <fieldset disabled={readOnly}>
      <Card className="mb-2">
        <Card.Header className="py-2 d-flex align-items-center justify-content-between">
          <span className="small fw-semibold">
            Advertiser &amp; Products
            {domainAdvertiserCount > 1 && (
              <Badge bg="warning" text="dark" className="ms-2">
                {domainAdvertiserCount} for this domain
              </Badge>
            )}
          </span>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => setShowAddAdvertiser(true)}
            >
              <Plus size={12} className="me-1" />
              Add Advertiser
            </Button>
            {selectedAdvertiser && (
              <Button
                size="sm"
                variant={
                  domainAdvertiserCount > 1 ? "warning" : "outline-secondary"
                }
                onClick={() => setShowChangeAdvertiser(true)}
              >
                <Search size={12} className="me-1" />
                Change advertiser
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="py-2">
          {advertisers.error && (
            <Alert variant="danger" className="py-2 small">
              {apiErrorMessage(
                advertisers.error,
                "Failed to load advertisers."
              )}
            </Alert>
          )}
          {actionError && (
            <Alert
              variant="danger"
              className="py-2 small"
              dismissible
              onClose={() => setActionError(null)}
            >
              {actionError}
            </Alert>
          )}

          {advertisers.loading && !selectedAdvertiser ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : !selectedAdvertiser ? (
            <div className="text-center py-3">
              <div className="text-muted small">
                No advertiser found for <strong>{domain}</strong>. Use "Add
                Advertiser" above to create one.
              </div>
            </div>
          ) : (
            <>
              <div className="d-flex flex-wrap gap-3 align-items-center mb-2">
                <div>
                  <div className="fw-semibold small">
                    {selectedAdvertiser.name}{" "}
                    <Badge bg="secondary">
                      {selectedAdvertiser.affiliateNetwork}
                    </Badge>
                  </div>
                  <small className="text-muted">
                    ID: {selectedAdvertiser.advertiserId}
                  </small>
                </div>
                <div className="small">
                  <div className="text-muted">Domain</div>
                  {selectedAdvertiser.domain || "-"}
                </div>
                <div className="small">
                  <div className="text-muted">Region</div>
                  {selectedAdvertiser.region || "-"}
                </div>
                <div className="small">
                  <div className="text-muted">Currency</div>
                  {selectedAdvertiser.currency || "-"}
                </div>
                <div className="small">
                  <div className="text-muted">Products</div>
                  {selectedAdvertiser.productCount}
                </div>
                {selectedAdvertiser.websiteUrl && (
                  <a
                    href={selectedAdvertiser.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small text-decoration-none"
                  >
                    <ExternalLink size={12} className="me-1" />
                    Website
                  </a>
                )}
              </div>

              <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                <InputGroup size="sm" style={{ width: 260 }}>
                  <Form.Control
                    placeholder="Search products…"
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setProductOffset(0);
                        setSubmittedQuery(productQuery.trim());
                      }
                    }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setProductOffset(0);
                      setSubmittedQuery(productQuery.trim());
                    }}
                  >
                    <Search size={12} />
                  </Button>
                </InputGroup>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => setShowAddProduct(true)}
                >
                  <Plus size={12} className="me-1" />
                  Add Product Manually
                </Button>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => setShowScrapeUrl(true)}
                >
                  <Link2 size={12} className="me-1" />
                  Fetch &amp; Analyse (from URL)
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => products.refetch().catch(() => undefined)}
                  disabled={products.loading}
                >
                  <RefreshCw size={12} />
                </Button>
              </div>

              {products.error && (
                <Alert variant="danger" className="py-2 small">
                  {apiErrorMessage(products.error, "Failed to load products.")}
                </Alert>
              )}

              {products.loading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : productRows.length === 0 ? (
                <div className="text-muted small py-2">
                  No products for this advertiser.
                </div>
              ) : (
                <>
                  <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    <Table hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Manufacturer</th>
                          <th>Categories</th>
                          <th>Price</th>
                          <th>Stock</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {productRows.map((product) => (
                          <tr key={product.id}>
                            <td className="small">
                              <div
                                className="text-truncate fw-semibold"
                                style={{ maxWidth: 240 }}
                                title={product.name}
                              >
                                {product.name}
                              </div>
                              {product.productUrl && (
                                <a
                                  href={product.productUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-decoration-none"
                                >
                                  <ExternalLink size={12} className="me-1" />
                                  View
                                </a>
                              )}
                            </td>
                            <td className="small">
                              {product.manufacturer || "-"}
                            </td>
                            <td
                              className="small text-truncate"
                              style={{ maxWidth: 200 }}
                              title={(product.categories || []).join(", ")}
                            >
                              {(product.categories || []).join(", ") || "-"}
                            </td>
                            <td className="small">{formatPrice(product)}</td>
                            <td>
                              <Badge
                                bg={product.inStock ? "success" : "secondary"}
                              >
                                {product.inStock ? "In stock" : "Out"}
                              </Badge>
                            </td>
                            <td className="text-nowrap">
                              <Button
                                size="sm"
                                variant="outline-success"
                                className="me-1"
                                onClick={() => handleAddTarget(product)}
                                disabled={
                                  addTargetDisabled ||
                                  addingTargetId !== null ||
                                  targetedProductKeys?.has(
                                    product.canonicalKey || product.id
                                  )
                                }
                              >
                                {addingTargetId === product.id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    {targetedProductKeys?.has(
                                      product.canonicalKey || product.id
                                    ) ? (
                                      "Target added"
                                    ) : (
                                      <>
                                        <Plus size={12} className="me-1" />
                                        Add Target
                                      </>
                                    )}
                                  </>
                                )}
                              </Button>
                              {product.affiliateNetwork === MANUAL_NETWORK && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  title="Remove manual product"
                                  onClick={() => handleDeleteProduct(product)}
                                  disabled={deleteProduct.loading}
                                >
                                  <X size={12} />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <small className="text-muted">
                      {productOffset + 1}–
                      {Math.min(productOffset + PAGE_SIZE, productTotal)} of{" "}
                      {productTotal}
                    </small>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={!hasPrev}
                        onClick={() =>
                          setProductOffset((prev) =>
                            Math.max(0, prev - PAGE_SIZE)
                          )
                        }
                      >
                        Prev
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={!hasNext}
                        onClick={() =>
                          setProductOffset((prev) => prev + PAGE_SIZE)
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </Card.Body>

        <AddAdvertiserModal
          show={showAddAdvertiser}
          domain={domain}
          onHide={() => setShowAddAdvertiser(false)}
          onCreated={(advertiser) => {
            setShowAddAdvertiser(false);
            setSelectedAdvertiser(advertiser);
            advertisers.refetch().catch(() => undefined);
          }}
        />

        <ChangeAdvertiserModal
          show={showChangeAdvertiser}
          domain={domain}
          currentId={selectedAdvertiser?.advertiserId}
          onHide={() => setShowChangeAdvertiser(false)}
          onSelect={handleSelectAdvertiser}
        />

        {selectedAdvertiser && (
          <>
            <AddProductModal
              show={showAddProduct}
              advertiser={selectedAdvertiser}
              onHide={() => setShowAddProduct(false)}
              onCreated={handleProductCreated}
            />
            <ScrapeUrlModal
              show={showScrapeUrl}
              advertiser={selectedAdvertiser}
              onHide={() => setShowScrapeUrl(false)}
              onCreated={handleProductCreated}
            />
          </>
        )}
      </Card>
    </fieldset>
  );
};
